/**
 * UploadInvoiceModal — upload / amend a per-account fee invoice (#17 invoice slice).
 *
 * Two modes:
 *   - 'upload' (frozen row → issue): PDF required, amounts required.
 *   - 'amend'  (issued/settled row): amounts pre-filled, PDF optional (omit to
 *     keep the existing file). Mirrors the AddAccountPartyModal shape (Mantine
 *     <Modal> + <form onSubmit={e => { e.stopPropagation(); ... }}>) so the inner
 *     submit never bubbles to a surrounding page form (C-113 guard).
 *
 * Money crosses the boundary as euros in the NumberInputs; the parent converts
 * to integer cents with eurToCents before calling the API (mirrors FeesSection).
 */
import {
  Alert, Button, FileInput, Group, Modal, NumberInput, Stack, Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconFileText, IconReceipt2 } from '@tabler/icons-react';
import * as React from 'react';

export type InvoiceModalMode = 'upload' | 'amend';

interface FormValues {
  netEur: number | '';
  totalEur: number | '';
  file: File | null;
}

export interface UploadInvoiceSubmit {
  netEur: number;
  totalEur: number;
  file: File | null;   // null only allowed in amend mode (keep existing)
}

interface Props {
  opened: boolean;
  onClose: () => void;
  mode: InvoiceModalMode;
  /** Friendly account label shown read-only at the top for confirmation. */
  accountLabel: string;
  /** Friendly month label (e.g. "March 2026") shown for confirmation. */
  monthLabel: string;
  /** Pre-fill amounts (euros) — used in amend mode. */
  initialNetEur?: number;
  initialTotalEur?: number;
  /** Called with the validated values. The parent converts €→cents and calls the API. */
  onSubmit: (values: UploadInvoiceSubmit) => void;
  submitting: boolean;
}

export function UploadInvoiceModal({
  opened, onClose, mode, accountLabel, monthLabel,
  initialNetEur, initialTotalEur, onSubmit, submitting,
}: Props) {
  const isAmend = mode === 'amend';

  const form = useForm<FormValues>({
    initialValues: {
      netEur: initialNetEur ?? '',
      totalEur: initialTotalEur ?? '',
      file: null,
    },
    validate: {
      netEur: (v) => (typeof v === 'number' && v >= 0 ? null : 'Enter the invoiced net amount'),
      totalEur: (v) => (typeof v === 'number' && v >= 0 ? null : 'Enter the invoiced total amount'),
      file: (v) => (!isAmend && !v ? 'A PDF is required' : null),
    },
  });

  // Reset the form to the current mode's defaults whenever the modal opens.
  React.useEffect(() => {
    if (opened) {
      form.setValues({
        netEur: initialNetEur ?? '',
        totalEur: initialTotalEur ?? '',
        file: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, initialNetEur, initialTotalEur, mode]);

  // C-113 guard: the modal renders within the page's React subtree, so without
  // stopPropagation the inner submit would also fire any surrounding form.
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    form.onSubmit((values) => {
      onSubmit({
        netEur: values.netEur as number,
        totalEur: values.totalEur as number,
        file: values.file,
      });
    })(e);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      title={isAmend ? 'Amend invoice' : 'Upload invoice'}
    >
      <form onSubmit={handleFormSubmit}>
        <Stack>
          <Alert color="blue" variant="light" icon={<IconReceipt2 size={16} />}>
            <Text size="sm" fw={500}>{accountLabel}</Text>
            <Text size="xs" c="dimmed">{monthLabel}</Text>
          </Alert>

          <FileInput
            label="Invoice PDF"
            placeholder={isAmend ? 'Leave empty to keep the current PDF' : 'Select the invoice PDF'}
            accept="application/pdf"
            leftSection={<IconFileText size={16} />}
            required={!isAmend}
            clearable
            value={form.values.file}
            onChange={(f) => form.setFieldValue('file', f)}
            error={form.errors.file as string | undefined}
          />

          <Group grow>
            <NumberInput
              label="Invoiced Net (ex-VAT) €"
              placeholder="0.00"
              required
              min={0}
              decimalScale={2}
              fixedDecimalScale
              thousandSeparator=","
              prefix="€"
              value={form.values.netEur}
              onChange={(v) => form.setFieldValue('netEur', typeof v === 'number' ? v : (v === '' ? '' : Number(v)))}
              error={form.errors.netEur as string | undefined}
            />
            <NumberInput
              label="Invoiced Total (incl-VAT) €"
              placeholder="0.00"
              required
              min={0}
              decimalScale={2}
              fixedDecimalScale
              thousandSeparator=","
              prefix="€"
              value={form.values.totalEur}
              onChange={(v) => form.setFieldValue('totalEur', typeof v === 'number' ? v : (v === '' ? '' : Number(v)))}
              error={form.errors.totalEur as string | undefined}
            />
          </Group>

          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button type="submit" loading={submitting}>
              {isAmend ? 'Save amendment' : 'Upload invoice'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
